import { useQuery } from "@tanstack/react-query";

import {
  IDexcomIntegration,
  IDexcomSyncLog,
} from "../../../@types/admin/AdminDexcomIntegration";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { dexcomIntegrationService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export const DEXCOM_INTEGRATIONS_QUERY_KEY = "admin-dexcom-integrations";

/**
 * Every Dexcom connection (EN-5232). A live operational view, so it must not
 * go stale between visits (the global QueryClient disables refetch-on-mount).
 */
export function useDexcomIntegrations() {
  const { data, isLoading, isError } = useQuery<
    IPaginatedResponse<IDexcomIntegration[]>
  >({
    queryKey: [DEXCOM_INTEGRATIONS_QUERY_KEY],
    queryFn: async () =>
      ensureOk(await dexcomIntegrationService.getIntegrations()),
    refetchOnMount: "always",
  });

  return { integrations: data?.result ?? [], isLoading, isError };
}

/** One page of a connection's sync history, keyed under the list so Refresh reloads it. */
export function useDexcomSyncLogs(
  integrationId: string,
  page: number,
  pageSize: number
) {
  const { data, isLoading, isError } = useQuery<
    IPaginatedResponse<IDexcomSyncLog[]>
  >({
    queryKey: [
      DEXCOM_INTEGRATIONS_QUERY_KEY,
      "sync-logs",
      integrationId,
      page,
      pageSize,
    ],
    queryFn: async () =>
      ensureOk(
        await dexcomIntegrationService.getSyncLogs(
          integrationId,
          page,
          pageSize
        )
      ),
    refetchOnMount: "always",
  });

  return { logs: data?.result ?? [], page: data, isLoading, isError };
}
